import React, { useMemo } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getCompany, getCompanyBankAccounts, getCompanyAchSettings, getSignatories, getYtdEntries } from 'services/company';
import { getPaySchedules } from 'services/paySchedules';
import { getWorkplaces } from 'services/workplaces';
import { getEmployees, getEmployeeBankAccounts } from 'services/employees';
import { getDepartments } from 'services/departments';
import { getActiveCompanyId } from 'utils/companyContext';

function asArray(raw) {
    return Array.isArray(raw) ? raw : (raw?.data ?? []);
}

function isActiveEmployee(e) {
    return e?.active !== false && e?.isActive !== false && e?.status !== 'inactive';
}

function hasCompanyDwollaFunding(achSettings) {
    if (!achSettings) return false;
    if (typeof achSettings === 'string') {
        try {
            const p = JSON.parse(achSettings);
            return !!(p?.dwollaFundingSourceUrl || p?.settings?.dwollaFundingSourceUrl);
        } catch {
            return achSettings.includes('dwollaFundingSourceUrl');
        }
    }
    return !!(achSettings?.settings?.dwollaFundingSourceUrl || achSettings?.dwollaFundingSourceUrl);
}

function isCompanyBank(acct, companyId) {
    const ownerType = (acct?.ownerType || acct?.OwnerType || '').toLowerCase();
    const ownerId = acct?.ownerId || acct?.OwnerId;
    if (ownerType === 'company') return true;
    if (ownerType && ownerType !== 'company') return false;
    return ownerId === companyId;
}

/** Shared setup / readiness checks for dashboard, get-ready, and payroll gates. */
export function useSetupChecks() {
    const companyId = getActiveCompanyId();
    const hasCompany = !!companyId;
    const { data: company } = useQuery({ queryKey: ['company', companyId], queryFn: getCompany, staleTime: 60000, enabled: hasCompany });
    const { data: bankAccountsRaw } = useQuery({ queryKey: ['company-bank-accounts', companyId], queryFn: getCompanyBankAccounts, staleTime: 60000, enabled: hasCompany });
    const { data: achSettings } = useQuery({ queryKey: ['company-ach-settings', companyId], queryFn: getCompanyAchSettings, staleTime: 60000, enabled: hasCompany });
    const { data: schedulesRaw } = useQuery({ queryKey: ['pay-schedules', companyId], queryFn: getPaySchedules, staleTime: 60000, enabled: hasCompany });
    const { data: workplacesRaw } = useQuery({ queryKey: ['workplaces', companyId], queryFn: getWorkplaces, staleTime: 60000, enabled: hasCompany });
    const { data: employeesRaw } = useQuery({ queryKey: ['employees', companyId], queryFn: getEmployees, staleTime: 60000, enabled: hasCompany });
    const { data: departmentsRaw } = useQuery({ queryKey: ['departments', companyId], queryFn: getDepartments, staleTime: 60000, enabled: hasCompany });
    const { data: signatoriesRaw } = useQuery({ queryKey: ['signatories', companyId], queryFn: getSignatories, staleTime: 60000, enabled: hasCompany });
    const { data: ytdRaw } = useQuery({ queryKey: ['ytd-entries', companyId], queryFn: getYtdEntries, staleTime: 60000, enabled: hasCompany });

    const allBanks = asArray(bankAccountsRaw);
    const companyBanks = allBanks.filter(b => isCompanyBank(b, companyId));
    const schedules = asArray(schedulesRaw);
    const workplaces = asArray(workplacesRaw);
    const employees = asArray(employeesRaw);
    const departments = asArray(departmentsRaw);
    const signatories = asArray(signatoriesRaw);
    const ytdEntries = asArray(ytdRaw);
    const activeEmployees = employees.filter(isActiveEmployee);

    const bankAccountQueries = useQueries({
        queries: activeEmployees.slice(0, 40).map(e => ({
            queryKey: ['employee-bank-accounts', e.id],
            queryFn: () => getEmployeeBankAccounts(e.id),
            staleTime: 60000,
            enabled: hasCompany && activeEmployees.length > 0,
        })),
    });

    const employeesWithBanks = activeEmployees.slice(0, 40).filter((_, i) =>
        asArray(bankAccountQueries[i]?.data).length > 0
    );
    const employeeBanksLoading = bankAccountQueries.some(q => q.isLoading);

    const hasAddress = !!(company?.addressLine1 || company?.address || company?.AddressLine1);
    const hasEin = !!(company?.ein || company?.Ein);
    const hasDepositSchedule = !!(company?.federalDepositSchedule || company?.FederalDepositSchedule);
    const hasSuta = !!(company?.sutaRate || company?.SutaRate);
    const hasCompanyFunding = companyBanks.length > 0 || hasCompanyDwollaFunding(achSettings);

    const checks = [
        {
            id: 'company-info',
            label: 'Company Information',
            description: 'Legal name, EIN, and address',
            done: !!(hasEin && hasAddress),
            hash: 'company-information',
            required: true,
            order: 1,
        },
        {
            id: 'federal-taxes',
            label: 'Federal & State Taxes',
            description: 'Federal deposit schedule and SUTA rate',
            done: !!(hasEin && hasDepositSchedule && hasSuta),
            hash: 'federal-state-taxes',
            required: true,
            order: 2,
        },
        {
            id: 'pay-schedules',
            label: 'Pay Schedules',
            description: 'At least one pay schedule (weekly, bi-weekly, etc.)',
            done: schedules.length > 0,
            hash: 'pay-schedules',
            required: true,
            order: 3,
        },
        {
            id: 'workplaces',
            label: 'Workplaces',
            description: 'At least one workplace location',
            done: workplaces.length > 0,
            hash: 'workplaces',
            required: true,
            order: 4,
        },
        {
            id: 'employees',
            label: 'Employees Added',
            description: 'At least one active employee',
            done: activeEmployees.length > 0,
            hash: null,
            link: '/employees',
            required: true,
            order: 5,
        },
        {
            id: 'bank-account',
            label: 'Withdrawal Bank Account',
            description: 'Company bank on file (Dwolla still required for ACH approve)',
            done: hasCompanyFunding,
            hash: 'withdrawal-bank-account',
            required: true,
            order: 6,
        },
        {
            id: 'departments',
            label: 'Departments',
            description: 'At least one department for employee grouping',
            done: departments.length > 0,
            hash: 'departments',
            required: false,
            order: 7,
        },
        {
            id: 'employee-setup',
            label: 'Employee Setup',
            description: 'At least one employee has a bank account for direct deposit',
            done: !employeeBanksLoading && employeesWithBanks.length > 0,
            hash: 'employee-setup',
            required: false,
            order: 8,
        },
        {
            id: 'workers-comp',
            label: 'Workers Compensation',
            description: 'Workers comp carrier and policy number',
            done: !!(company?.workersCompCarrier || company?.workersCompPolicyNumber),
            hash: 'workers-compensation',
            required: false,
            order: 9,
        },
        {
            id: 'prior-payroll',
            label: 'Prior Payroll (YTD)',
            description: 'Enter YTD balances if switching from another provider',
            done: ytdEntries.length > 0,
            hash: 'prior-payroll',
            required: false,
            order: 10,
        },
        {
            id: 'filing-auth',
            label: 'Filing Authorization',
            description: 'At least one authorized signatory on file',
            done: signatories.length > 0,
            hash: 'filling-authorization',
            required: false,
            order: 11,
        },
    ];

    const completed = checks.filter(c => c.done).length;
    const total = checks.length;
    const required = checks.filter(c => c.required);
    const requiredDone = required.filter(c => c.done).length;
    const allRequiredDone = requiredDone === required.length;
    const missingRequired = required.filter(c => !c.done);

    /** ACH approve hard-gate blockers (company-level). Payee bank gaps checked separately. */
    const achCompanyBlockers = useMemo(() => {
        const blockers = [];
        if (!hasEin) blockers.push('Company EIN is required before ACH payroll.');
        if (!hasAddress) blockers.push('Company address is required before ACH payroll.');
        if (!hasCompanyDwollaFunding(achSettings)) {
            blockers.push('Verified company withdrawal bank (Dwolla funding source) is required before ACH.');
        }
        if (schedules.length === 0) blockers.push('At least one pay schedule is required before ACH.');
        if (workplaces.length === 0) blockers.push('At least one workplace is required before ACH.');
        if (activeEmployees.length === 0) blockers.push('At least one active employee is required before ACH.');
        return blockers;
    }, [hasEin, hasAddress, achSettings, schedules.length, workplaces.length, activeEmployees.length]);

    return {
        checks,
        completed,
        total,
        requiredDone,
        requiredTotal: required.length,
        allRequiredDone,
        missingRequired,
        achCompanyBlockers,
        hasVerifiedDwollaFunding: hasCompanyDwollaFunding(achSettings),
        company,
    };
}

export function isDirectDepositMethod(method) {
    if (!method) return false;
    const s = String(method).trim().toLowerCase();
    return ['direct_deposit', 'direct deposit', 'ach', 'dd', 'bank', 'deposit'].includes(s);
}

// ── Dashboard card ────────────────────────────────────────────────────────────
export function SetupProgressCard() {
    const navigate = useNavigate();
    const { checks, completed, total, requiredDone, requiredTotal, allRequiredDone } = useSetupChecks();
    const pct = Math.round((requiredDone / Math.max(requiredTotal, 1)) * 100);

    // Hide when all required steps are done (optional incomplete is fine)
    if (allRequiredDone) return null;

    return (
        <div style={{
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 1px 8px rgba(0,0,0,0.08)',
            padding: '20px 24px',
            marginBottom: 24,
            border: '1px solid #e5e7eb',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <h5 style={{ margin: 0, fontWeight: 700, color: '#1a3a5c', fontSize: 16 }}>
                        Get ready for payroll
                    </h5>
                    <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' }}>
                        {requiredDone} of {requiredTotal} required steps complete
                        <span style={{ color: '#1B75F0', fontWeight: 600 }}> — required steps missing</span>
                        {completed < total && (
                            <span style={{ color: '#9ca3af' }}> · {completed} of {total} total</span>
                        )}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => navigate('/dashboard/get-ready')}
                    style={{ background: '#1B75F0', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                >
                    Continue setup
                </button>
            </div>

            <div style={{ background: '#f3f4f6', borderRadius: 99, height: 8, marginBottom: 16, overflow: 'hidden' }}>
                <div style={{
                    height: '100%', borderRadius: 99,
                    width: `${pct}%`,
                    background: '#1B75F0',
                    transition: 'width 0.4s ease',
                }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
                {checks.filter(c => c.required).map(item => (
                    <div
                        key={item.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => item.link
                            ? navigate(item.link)
                            : navigate(`/dashboard/manage-company${item.hash ? '#' + item.hash : ''}`)}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.click(); }}
                        style={{
                            display: 'flex', alignItems: 'flex-start', gap: 10,
                            padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                            background: item.done ? '#f0fdf4' : '#EFF6FF',
                            border: `1px solid ${item.done ? '#bbf7d0' : '#bfdbfe'}`,
                        }}
                    >
                        <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>
                            {item.done ? '✅' : '⚠️'}
                        </span>
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: item.done ? '#15803d' : '#374151' }}>
                                {item.label}
                            </div>
                            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{item.description}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/** Soft warn before creating payroll when required setup is incomplete. */
export function SoftSetupWarnModal({ open, missingRequired, onContinue, onCancel, onGoSetup }) {
    if (!open) return null;
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#fff', borderRadius: 12, maxWidth: 480, width: '100%', padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                <h5 style={{ margin: '0 0 8px', fontWeight: 700, color: '#1a3a5c' }}>Company setup incomplete</h5>
                <p style={{ margin: '0 0 12px', fontSize: 14, color: '#4b5563' }}>
                    You can still create a draft payroll. ACH approval will be blocked until required items and funding are ready.
                </p>
                <ul style={{ margin: '0 0 16px', paddingLeft: 18, fontSize: 13, color: '#374151' }}>
                    {missingRequired.map(m => (
                        <li key={m.id} style={{ marginBottom: 4 }}>{m.label}</li>
                    ))}
                </ul>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <button type="button" className="btn btn-light" onClick={onCancel}>Cancel</button>
                    <button type="button" className="btn btn-outline-primary" onClick={onGoSetup}>Continue setup</button>
                    <button type="button" className="btn btn-primary" onClick={onContinue}>Continue anyway</button>
                </div>
            </div>
        </div>
    );
}

// ── Sidebar indicator ─────────────────────────────────────────────────────────
export function useSetupIndicators() {
    const { checks } = useSetupChecks();
    const byId = {};
    checks.forEach(c => {
        byId[c.id] = { done: c.done, required: !!c.required };
    });
    return byId;
}

export default useSetupChecks;
